import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation } from 'wouter';
import { FileText, Plus, AlertCircle, Edit3, Trash2, Share2, Eye, User } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { ShareModal } from '../components/ShareModal';
import { api } from '../lib/api';
import { useAuth } from '../hooks/use-auth';
import { v4 as uuid } from 'uuid';
import { autoSizeElement } from '../lib/measureElement';

export function SongDetail() {
  const params = useParams<{ bandId: string; songId: string }>();
  const bandId = Number(params.bandId);
  const songId = Number(params.songId);
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editArtist, setEditArtist] = useState('');
  const [editKey, setEditKey] = useState('');
  const [editTempo, setEditTempo] = useState('');
  const [editTags, setEditTags] = useState('');

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const { data: song } = useQuery({
    queryKey: ['song', songId],
    queryFn: () => api.songs.get(songId),
  });

  const { data: baseCharts = [] } = useQuery({
    queryKey: ['baseCharts', songId],
    queryFn: () => api.charts.getBaseForSong(songId),
  });

  const baseChart = baseCharts[0] || null;

  const { data: personalVariant } = useQuery({
    queryKey: ['personalVariant', baseChart?.id],
    queryFn: () => api.charts.getPersonalForBase(baseChart!.id),
    enabled: !!baseChart,
  });

  const createBaseChart = useMutation({
    mutationFn: () => {
      const CANVAS_MARGIN = 36;
      const headerContent = {
        title: song?.title || '',
        artist: song?.artist || '',
        key: song?.defaultKey || '',
        bpm: song?.defaultTempo || null,
        protected: true,
      };
      const { width: hw, height: hh } = autoSizeElement('songHeader', headerContent, 800 * 0.5);
      const songHeaderElement = {
        id: uuid(),
        type: 'songHeader' as const,
        x: CANVAS_MARGIN,
        y: CANVAS_MARGIN,
        width: hw,
        height: hh,
        content: headerContent,
        zIndex: 0,
        manualSize: false,
      };
      return api.charts.createBase({
        songId,
        bandId,
        chartMetadata: {
          songTitle: song?.title || '',
          artist: song?.artist || '',
          tempo: song?.defaultTempo || null,
          musicalKey: song?.defaultKey || null,
          chordSystem: 'standard',
          sections: [],
          tags: song?.tags || [],
          hasNotes: false,
          hasLyrics: false,
        },
        canvasJson: { elements: [songHeaderElement], canvasWidth: 800, canvasHeight: 1100, zoom: 1 },
      });
    },
    onSuccess: (chart) => {
      queryClient.invalidateQueries({ queryKey: ['baseCharts', songId] });
      navigate(`/editor/base/${chart.id}`);
    },
    onError: () => {
      // Refresh in case the chart was created by another session and the UI is stale
      queryClient.invalidateQueries({ queryKey: ['baseCharts', songId] });
    },
  });

  const createPersonalVariant = useMutation({
    mutationFn: () => api.charts.createPersonal(baseChart!.id),
    onSuccess: (variant) => {
      queryClient.invalidateQueries({ queryKey: ['personalVariant', baseChart?.id] });
      navigate(`/editor/personal/${variant.id}`);
    },
  });

  const updateSong = useMutation({
    mutationFn: (data: any) => api.songs.update(songId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['song', songId] });
      queryClient.invalidateQueries({ queryKey: ['songs', bandId] });
      setShowEdit(false);
    },
  });

  const deleteSong = useMutation({
    mutationFn: () => api.songs.delete(songId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['songs', bandId] });
      navigate(`/bands/${bandId}/songs`);
    },
  });

  const { data: sharedVersions = [] } = useQuery({
    queryKey: ['sharedVersions', songId],
    queryFn: () => api.charts.getSharedForSong(songId),
    enabled: !!baseChart,
  });

  const hasBaseUpdated = personalVariant && personalVariant.baseVersionNumber != null && personalVariant.lastSeenBaseVersion < personalVariant.baseVersionNumber;

  function openEdit() {
    if (!song) return;
    setEditTitle(song.title);
    setEditArtist(song.artist || '');
    setEditKey(song.defaultKey || '');
    setEditTempo(song.defaultTempo?.toString() || '');
    setEditTags(song.tags?.join(', ') || '');
    setShowEdit(true);
  }

  return (
    <Layout bandId={bandId} bandName={band?.name} title={song?.title || 'Song'} showBack backTo={`/bands/${bandId}/songs`}>
      <div className="p-4 md:p-6 max-w-2xl mx-auto">
        {song && (
          <>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">{song.title}</h2>
                {song.artist && <p className="text-gray-400">{song.artist}</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {song.defaultKey && <span className="text-xs bg-primary-600/20 text-primary-300 px-2 py-1 rounded-full">Key: {song.defaultKey}</span>}
                  {song.defaultTempo && <span className="text-xs bg-primary-600/20 text-primary-300 px-2 py-1 rounded-full">{song.defaultTempo} BPM</span>}
                  {song.tags?.map((t: string) => (
                    <span key={t} className="text-xs bg-accent-600/20 text-accent-400 px-2 py-1 rounded-full">{t}</span>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={openEdit} className="p-2 hover:bg-surface-800 rounded-lg transition-colors">
                  <Edit3 className="w-4 h-4 text-gray-400" />
                </button>
                {band?.role === 'owner' && (
                  <button onClick={() => { if (confirm('Delete this song and all its charts?')) deleteSong.mutate(); }} className="p-2 hover:bg-red-900/30 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-surface-900 border border-surface-700 rounded-xl p-5">
                <h3 className="font-semibold mb-3">Base Chart</h3>
                {baseChart ? (
                  <div>
                    <p className="text-sm text-gray-400 mb-3">Version {baseChart.versionNumber}</p>
                    <button
                      onClick={() => navigate(`/editor/base/${baseChart.id}`)}
                      className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Open Base Chart
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-400 mb-3">No chart created yet for this song.</p>
                    {(band?.role === 'owner' || band?.role === 'admin') ? (
                      <>
                        <button
                          onClick={() => createBaseChart.mutate()}
                          disabled={createBaseChart.isPending}
                          className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          <Plus className="w-4 h-4" />
                          {createBaseChart.isPending ? 'Creating...' : 'Create Base Chart'}
                        </button>
                        {createBaseChart.isError && (
                          <p className="mt-2 text-sm text-red-400">
                            {(createBaseChart.error as Error)?.message?.includes('409')
                              ? 'This song already has a base chart.'
                              : 'Failed to create chart. Please try again.'}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Only admins and owners can create base charts.</p>
                    )}
                  </div>
                )}
              </div>

              {baseChart && (
                <div className="bg-surface-900 border border-surface-700 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-semibold">My Version</h3>
                    {hasBaseUpdated && (
                      <span className="flex items-center gap-1 text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" />
                        Base Chart Updated
                      </span>
                    )}
                  </div>
                  {personalVariant ? (
                    <div>
                      <p className="text-sm text-gray-400 mb-3">Version {personalVariant.versionNumber}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/editor/personal/${personalVariant.id}`)}
                          className="flex items-center gap-2 bg-accent-600 hover:bg-accent-500 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          <FileText className="w-4 h-4" />
                          Open My Version
                        </button>
                        <button
                          onClick={() => setShowShare(true)}
                          className="flex items-center gap-2 bg-surface-700 hover:bg-surface-600 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                        >
                          <Share2 className="w-4 h-4" />
                          Share
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-400 mb-3">Create your own version with personal notes and edits.</p>
                      <button
                        onClick={() => createPersonalVariant.mutate()}
                        disabled={createPersonalVariant.isPending}
                        className="flex items-center gap-2 bg-accent-600 hover:bg-accent-500 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
                      >
                        <Plus className="w-4 h-4" />
                        {createPersonalVariant.isPending ? 'Creating...' : 'Create My Version'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {sharedVersions.length > 0 && (
                <div className="bg-surface-900 border border-surface-700 rounded-xl p-5">
                  <h3 className="font-semibold mb-3">Shared Versions</h3>
                  <p className="text-sm text-gray-400 mb-3">Other band members have shared their versions of this chart with you.</p>
                  <div className="space-y-2">
                    {sharedVersions.map((sv: any) => (
                      <div key={sv.id} className="flex items-center justify-between p-3 bg-surface-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-accent-400" />
                          <div>
                            <span className="text-sm font-medium">{sv.ownerName}'s Version</span>
                            <span className="text-xs text-gray-500 ml-2">v{sv.versionNumber}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/editor/shared/${sv.id}`)}
                          className="flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </>
        )}

        <Modal open={showEdit} onClose={() => setShowEdit(false)} title="Edit Song">
          <form onSubmit={e => { e.preventDefault(); updateSong.mutate({ title: editTitle, artist: editArtist || null, defaultKey: editKey || null, defaultTempo: editTempo ? Number(editTempo) : null, tags: editTags ? editTags.split(',').map(t => t.trim()).filter(Boolean) : [] }); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
              <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Artist</label>
              <input value={editArtist} onChange={e => setEditArtist(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Key</label>
                <input value={editKey} onChange={e => setEditKey(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tempo</label>
                <input type="number" value={editTempo} onChange={e => setEditTempo(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tags (comma-separated)</label>
              <input value={editTags} onChange={e => setEditTags(e.target.value)} className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <button type="submit" disabled={updateSong.isPending} className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors text-sm">
              {updateSong.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </Modal>

        {personalVariant && (
          <ShareModal
            open={showShare}
            onClose={() => setShowShare(false)}
            personalVariantId={personalVariant.id}
            bandId={bandId}
          />
        )}
      </div>
    </Layout>
  );
}
